import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { brand, hexToRgb } from './brand';
import { formatCurrency } from './utils';

// Colores del PDF (arreglos RGB para jsPDF) tomados de la marca activa.
export const pdfColors = {
  band: hexToRgb(brand.colors.band),
  primary: hexToRgb(brand.colors.primary),
  onPrimary: hexToRgb(brand.colors.onPrimary),
  text: hexToRgb(brand.colors.text),
};

// Dibuja un polígono relleno (puntos absolutos en unidades de la marca) con jsPDF.
function fillPolygon(doc, pts, ox, oy, s) {
  const [first, ...rest] = pts;
  const segs = [];
  let px = first[0];
  let py = first[1];
  rest.forEach(([x, y]) => {
    segs.push([(x - px) * s, (y - py) * s]);
    px = x;
    py = y;
  });
  doc.lines(segs, ox + first[0] * s, oy + first[1] * s, [1, 1], 'F', true);
}

// Dibuja la «M» de la marca (si existe) en blanco con el triángulo interior en el acento.
function drawMark(doc, x, y, heightMm) {
  const { mark } = brand;
  const s = heightMm / mark.height;
  // La «M» ocupa de x=18 a x=98 en su caja; se alinea su borde izquierdo con x.
  const minX = Math.min(...mark.shapes.flat().map((p) => p[0]));
  const ox = x - minX * s;
  doc.setFillColor(...hexToRgb(brand.colors.accent));
  fillPolygon(doc, mark.accent, ox, y, s);
  doc.setFillColor(255, 255, 255);
  mark.shapes.forEach((shape) => fillPolygon(doc, shape, ox, y, s));
}

// Encabezado común de los PDF: banda de marca, nombre, subtítulo y título «COTIZACIÓN».
export function buildPdfHeader(doc) {
  doc.setFillColor(...pdfColors.band);
  doc.rect(0, 0, 210, 35, 'F');

  // La M (si la marca la tiene) va a la izquierda; el texto sigue centrado respecto a la página.
  if (brand.pdf.showMark && brand.mark) drawMark(doc, 14, 5.5, 24);

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(brand.pdf.title, 105, 15, { align: 'center' });
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(brand.pdf.subtitle, 105, 22, { align: 'center' });
  doc.setTextColor(...pdfColors.text);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('COTIZACIÓN', 105, 50, { align: 'center' });
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
}

const MARGIN_X = 14;
const CONTENT_W = 182;
const PAGE_BOTTOM = 274;

// Genera el PDF de una cotización/proforma. `quote` usa el mismo formato que el API:
// { created_at, user_name, user_code, client_*, notes, items: [{ product_code, product_name,
//   quantity, price_key, unit_price_applied, subtotal }], total_amount }.
// `settings` (opcional) permite sobrescribir la leyenda de validez: { validityText }.
export function generateQuotePdf(quote, settings = {}) {
  const doc = new jsPDF();
  buildPdfHeader(doc);

  const date = new Date(quote.created_at || Date.now()).toLocaleDateString('es-EC', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  let yPos = 60;
  const line = (text) => {
    doc.text(text, MARGIN_X, yPos);
    yPos += 6;
  };
  line(`Fecha: ${date}`);
  line(`Vendedor: ${quote.user_name || quote.user_code || 'N/A'}`);
  if (quote.client_name) line(`Cliente: ${quote.client_name}`);
  if (quote.client_id_number) line(`Cédula/RUC: ${quote.client_id_number}`);
  if (quote.client_city) line(`Ciudad: ${quote.client_city}`);
  if (quote.client_phone) line(`Teléfono: ${quote.client_phone}`);
  if (quote.client_email) line(`Email: ${quote.client_email}`);
  if (quote.client_address) line(`Dirección: ${quote.client_address}`);

  const priceLabel = (key) => (key === 'price_2' ? '2' : key === 'price_1' ? '1' : '-');
  const body = (quote.items || []).map((item) => [
    item.product_code,
    item.product_name,
    item.quantity,
    priceLabel(item.price_key),
    formatCurrency(item.unit_price_applied),
    formatCurrency(item.subtotal),
  ]);

  autoTable(doc, {
    startY: yPos + 4,
    margin: { top: 14, bottom: 16, left: MARGIN_X, right: MARGIN_X },
    head: [['Código', 'Producto', 'Cant.', 'P.', 'P. Unit.', 'Subtotal']],
    body,
    theme: 'grid',
    showHead: 'everyPage',
    rowPageBreak: 'avoid',
    headStyles: {
      fillColor: pdfColors.primary,
      textColor: pdfColors.onPrimary,
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: { top: 1.5, bottom: 1.5, left: 1.5, right: 1.5 },
    },
    alternateRowStyles: { fillColor: [248, 248, 248] },
    styles: {
      fontSize: 8.5,
      cellPadding: { top: 1.4, bottom: 1.4, left: 2, right: 2 },
      overflow: 'linebreak',
    },
    columnStyles: {
      0: { cellWidth: 32 },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 13, halign: 'center' },
      3: { cellWidth: 9, halign: 'center' },
      4: { cellWidth: 22, halign: 'right' },
      5: { cellWidth: 24, halign: 'right' },
    },
  });

  // Total (si no cabe, pasa a una página nueva)
  let finalY = doc.lastAutoTable.finalY + 10;
  if (finalY + 12 > PAGE_BOTTOM) {
    doc.addPage();
    finalY = 20;
  }
  doc.setFillColor(...pdfColors.band);
  doc.rect(120, finalY, 76, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(`TOTAL: ${formatCurrency(quote.total_amount)}`, 158, finalY + 8, { align: 'center' });
  let notesY = finalY + 12 + 10;

  // Observaciones: después del TOTAL y antes de la leyenda de validez
  const notes = (quote.notes || '').trim();
  if (notes) {
    const lineH = 4.5;
    const lines = doc.setFontSize(9).setFont('helvetica', 'normal').splitTextToSize(notes, CONTENT_W);
    if (notesY + 6 + lineH > PAGE_BOTTOM) {
      doc.addPage();
      notesY = 20;
    }
    doc.setTextColor(...pdfColors.text);
    doc.setFont('helvetica', 'bold');
    doc.text('Observaciones:', MARGIN_X, notesY);
    notesY += 6;
    doc.setFont('helvetica', 'normal');
    lines.forEach((l) => {
      if (notesY > PAGE_BOTTOM) {
        doc.addPage();
        notesY = 20;
      }
      doc.text(l, MARGIN_X, notesY);
      notesY += lineH;
    });
  }

  // Leyenda de validez (última página)
  doc.setTextColor(150, 150, 150);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(
    settings.validityText || 'Esta cotización es válida por 7 días. Precios sujetos a cambio sin previo aviso.',
    105,
    280,
    { align: 'center' }
  );

  return doc;
}
